import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast, type ToastContent } from 'react-toastify';
import Toast from '@/components/ui/Toast';
import Button from '@/components/ui/Button';
import { type ApiError } from '@/utils/ReqUtil';
import { authStore } from '@/store/authStore';
import { userStore } from '@/store/user/store';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import HelperDialog from '@/features/funnel/questionnaires/components/HelperDialog';
import Modal from '@/components/ui/modal/Modal';
import FunnelModalTitle from '@/components/ui/modal/funnel/FunnelModalTitle';
import type { FunnelNavigationResponse } from '@/types/api/shared';

interface ErrorProps {
  message?: string;
  retry?: () => void;
  error?: ApiError;
}

const Error: React.FC<ErrorProps> = ({ message = 'Something went wrong...', retry, error }) => {
  const navigate = useNavigate();
  const notFound = error?.type === 'notFound';
  const errorData = error?.data;
  const [redirectPath, setRedirectPath] = useState<string>('');
  const { accessToken } = authStore();
  const { user } = userStore();
  const location = useLocation();

  useAuthRedirect(redirectPath);

  if (notFound) {
    queueMicrotask(() => navigate('/404', { replace: true }));
    return null;
  }

  const getRedirectPath = (errorData: FunnelNavigationResponse) => {
    switch (errorData.path_type) {
      case 'user_questionnaire':
        return `/products/${errorData.product_slug}/user_questionnaires/${errorData.user_questionnaire_id}/questions/${errorData.question_id}`;
      case 'dissolving-tablet':
      case 'intro':
        return `/products/${errorData.product_slug}/${errorData.path_type}`;
      default:
        return `/${errorData.path_type}`;
    }
  };

  if (error?.type === 'unprocessableEntity' && errorData) {
    queueMicrotask(() => {
      if (error.verified) {
        setRedirectPath('order_history');
      } else {
        navigate(getRedirectPath(errorData), { replace: true });
        setTimeout(() => {
          toast.error(Toast as ToastContent, {
            toastId: '422-error-toast',
            position: 'top-center',
            className: 'border border-off-white',
            data: {
              title: 'Error Occurred',
              message: error.message,
            },
          });
        }, 1000);
      }
    });
    return null;
  }

  const handleGoToHome = () => {
    if (accessToken && error?.type && ['unprocessableEntity', 'forbidden'].includes(error?.type)) {
      if (user.registration_state === 'completed') {
        setRedirectPath('dashboard');
      } else {
        navigate('/user-demographics', { replace: true });
      }
    } else {
      navigate('/', { replace: true });
    }
  };

  const closeModal = () => {
    navigate('/', { replace: true });
  };

  const isResetPasswordPage = location.pathname.includes('reset-password');

  return (
    <>
      {isResetPasswordPage ? (
        <HelperDialog
          isOpen
          title={'You can’t access this page without coming from a password reset email.'}
          description={
            'If you do come from a password reset email, please make sure you used the full URL provided.'
          }
          buttonText={'Return to Login'}
          onClose={closeModal}
        />
      ) : (
        <Modal
          isOpen
          onClose={() => {}}
          title={<FunnelModalTitle title={message} titleClass="!text-subtitle tablet:-mt-[4px]" />}
        >
          {retry && (
            <Button
              role="button"
              className="cursor-pointer shadow-subtle-gray mt-8 tablet:mt-24 !font-bold tablet:shadow-black-cta-shad"
              onClick={retry}
              size="xsmall"
              rounded="rounded-full tablet:rounded-[16px]"
            >
              Retry
            </Button>
          )}
          <Button
            type="button"
            className="mt-8 tablet:mt-12 cursor-pointer border border-off-white !text-ashgray font-normal tablet:!h-[65px] tablet:shadow-small-shad"
            bgcolor="bg-funnel-bg hover:bg-funnel-bg"
            textcolor="text-ashgray hover:text-ashgray"
            size="xsmall"
            rounded="rounded-full tablet:rounded-[16px]"
            onClick={handleGoToHome}
          >
            Go Home
          </Button>
        </Modal>
      )}
    </>
  );
};

export default Error;
